import type { Unidad } from '@condo/contracts';
import { formatClp } from '@/lib/format/currency';
import { formatAlicuotaFraction } from '@/lib/format/number';

interface Props {
  unidades: Unidad[];
  montoTotal: number;
}

export function ProrrateoPreview({ unidades, montoTotal }: Props) {
  if (unidades.length === 0 || montoTotal === 0) {
    return (
      <div className="grid h-48 place-items-center rounded-md border border-dashed border-[var(--color-hairline-strong)] bg-[var(--color-paper-dim)] px-6 text-center text-sm text-[var(--color-ink-muted)]">
        Ingresa un monto para previsualizar el prorrateo por unidad.
      </div>
    );
  }
  const rows = unidades.map((u) => ({
    id: u.id,
    numero: u.numero,
    alicuota: u.alicuota,
    monto: Math.round(montoTotal * u.alicuota),
  }));
  const sumaCalculada = rows.reduce((s, r) => s + r.monto, 0);
  const diferencia = montoTotal - sumaCalculada;
  const suggestion = 'La suma calculada puede diferir del total en +/- unos pesos debido al redondeo.';

  return (
    <div className="overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)]">
      <div className="flex items-center justify-between border-b border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-4 py-3">
        <div>
          <p className="eyebrow">Vista previa</p>
          <p className="font-display text-sm font-semibold">
            Prorrateo por alicuota
          </p>
        </div>
        <div className="text-right">
          <p className="eyebrow">Total</p>
          <p className="font-display-tight tabular text-xl font-semibold">
            {formatClp(montoTotal)}
          </p>
        </div>
      </div>
      <div className="max-h-80 overflow-y-auto">
        <table className="w-full text-sm tabular">
          <thead className="sticky top-0 bg-[var(--color-paper)]">
            <tr className="border-b border-[var(--color-hairline)]">
              <th className="px-4 py-2 text-left text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                Unidad
              </th>
              <th className="px-4 py-2 text-right text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                Alicuota
              </th>
              <th className="px-4 py-2 text-right text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                Monto
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-b border-[var(--color-hairline)] last:border-0 transition-colors hover:bg-[var(--color-paper-dim)]"
              >
                <td className="px-4 py-2 font-display font-semibold text-[var(--color-ink)]">
                  {r.numero}
                </td>
                <td className="px-4 py-2 text-right text-[var(--color-ink-muted)]">
                  {formatAlicuotaFraction(r.alicuota)}
                </td>
                <td className="px-4 py-2 text-right font-display font-semibold text-[var(--color-ink)]">
                  {formatClp(r.monto)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--color-ink)]">
              <td className="px-4 py-2 font-display text-xs font-semibold uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                Calculado
              </td>
              <td />
              <td className="px-4 py-2 text-right font-display font-semibold">
                {formatClp(sumaCalculada)}
              </td>
            </tr>
            {diferencia !== 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-2 text-right text-[0.6875rem] text-[var(--color-ink-faint)]"
                >
                  Diferencia por redondeo: {formatClp(Math.abs(diferencia))}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      <p className="border-t border-[var(--color-hairline)] bg-[var(--color-paper-dim)] px-4 py-2 text-[0.6875rem] text-[var(--color-ink-faint)]">
        {suggestion}
      </p>
    </div>
  );
}
